import React, { useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
} from 'react-native';
import PlatformIcon from '../common/PlatformIcon';

const MODULE_COLOR = '#F97316';

export interface TodoItem {
  id: string;
  title: string;
  description?: string | null;
  completed: boolean;
  affiliate_url?: string | null;
  action_label?: string | null;
  created_at?: string;
  creator?: {
    display_name?: string | null;
  } | null;
}

export interface TodoSectionProps {
  todos: TodoItem[];
  onToggleTodo?: (todoId: string, currentStatus: boolean) => void;
  onAddPress?: () => void;
  onActionPress?: (todo: TodoItem) => void;
}

const formatDate = (dateString?: string | null): string => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return `${date.getFullYear()}/${date.getMonth() + 1}/${date.getDate()}`;
};

const TodoSection: React.FC<TodoSectionProps> = ({
  todos,
  onToggleTodo,
  onAddPress,
  onActionPress,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const displayTodos = isExpanded ? todos : todos.slice(0, 3);

  return (
    <View style={styles.section}>
      <View style={styles.sectionHeader}>
        <View style={styles.sectionTitleRow}>
          <PlatformIcon name="check-circle" size={18} color={MODULE_COLOR} family="MaterialIcons" />
          <Text style={styles.sectionTitle}>待办事项</Text>
        </View>
        {onAddPress && (
          <TouchableOpacity style={styles.addButton} onPress={onAddPress}>
            <PlatformIcon name="add" size={14} color={MODULE_COLOR} family="MaterialIcons" />
            <Text style={styles.addButtonText}>添加</Text>
          </TouchableOpacity>
        )}
      </View>

      {todos.length > 0 ? (
        <>
          {displayTodos.map((todo) => (
            <TouchableOpacity
              key={todo.id}
              style={styles.todoItem}
              onPress={() => onToggleTodo?.(todo.id, todo.completed)}
            >
              <View style={styles.todoCheckbox}>
                <PlatformIcon
                  name={todo.completed ? 'check-circle' : 'radio-button-unchecked'}
                  size={22}
                  color={todo.completed ? '#10B981' : '#D1D5DB'}
                  family="MaterialIcons"
                />
              </View>
              <View style={styles.todoContent}>
                <Text style={[styles.todoTitle, todo.completed && styles.todoCompleted]}>
                  {todo.title}
                </Text>
                {todo.description && (
                  <Text style={styles.todoDescription}>{todo.description}</Text>
                )}
                <Text style={styles.todoMeta}>
                  {todo.creator?.display_name || '未知'} • {formatDate(todo.created_at)}
                </Text>
              </View>
              {todo.affiliate_url && (
                <TouchableOpacity
                  style={styles.todoAction}
                  onPress={() => onActionPress?.(todo)}
                >
                  <Text style={styles.todoActionText}>{todo.action_label || '查看'}</Text>
                </TouchableOpacity>
              )}
            </TouchableOpacity>
          ))}
          {todos.length > 3 && (
            <TouchableOpacity style={styles.expandButton} onPress={() => setIsExpanded(!isExpanded)}>
              <Text style={styles.expandButtonText}>
                {isExpanded ? '收起' : `展开更多 (${todos.length - 3})`}
              </Text>
              <PlatformIcon
                name={isExpanded ? 'expand-less' : 'expand-more'}
                size={16}
                color={MODULE_COLOR}
                family="MaterialIcons"
              />
            </TouchableOpacity>
          )}
        </>
      ) : (
        <View style={styles.emptyState}>
          <PlatformIcon name="check-circle" size={40} color="#D1D5DB" family="MaterialIcons" />
          <Text style={styles.emptyStateText}>暂无待办事项</Text>
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  section: {
    backgroundColor: '#fff',
    borderRadius: 16,
    padding: 16,
    marginBottom: 16,
  },
  sectionHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
  },
  sectionTitleRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: '700',
    color: '#1F2937',
    marginLeft: 6,
  },
  addButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#FFF7ED',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: MODULE_COLOR,
  },
  addButtonText: {
    color: MODULE_COLOR,
    fontSize: 12,
    fontWeight: '600',
    marginLeft: 4,
  },
  todoItem: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#F3F4F6',
  },
  todoCheckbox: {
    marginRight: 10,
    marginTop: 2,
  },
  todoContent: {
    flex: 1,
  },
  todoTitle: {
    fontSize: 15,
    fontWeight: '500',
    color: '#1F2937',
  },
  todoCompleted: {
    textDecorationLine: 'line-through',
    color: '#9CA3AF',
  },
  todoDescription: {
    fontSize: 13,
    color: '#6B7280',
    marginTop: 2,
  },
  todoMeta: {
    fontSize: 12,
    color: '#9CA3AF',
    marginTop: 4,
  },
  todoAction: {
    backgroundColor: '#FFF7ED',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: MODULE_COLOR,
  },
  todoActionText: {
    color: MODULE_COLOR,
    fontSize: 12,
    fontWeight: '600',
  },
  expandButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 8,
    marginTop: 8,
  },
  expandButtonText: {
    fontSize: 13,
    color: MODULE_COLOR,
    fontWeight: '500',
  },
  emptyState: {
    alignItems: 'center',
    paddingVertical: 24,
  },
  emptyStateText: {
    fontSize: 15,
    color: '#9CA3AF',
    marginTop: 8,
  },
});

export default TodoSection;
